import { Component, computed, EventEmitter, Input, Output, SimpleChanges, OnChanges, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TabsModule } from 'primeng/tabs';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxComponent } from '@components/checkbox';
import { InputComponent } from '@components/input';
import { MsfSelectPaginated } from '@components/select/paginated/select-paginated';
import { DatePickerComponent } from '@components/date-picker';
import { UserService } from '@6grain/masofa-monolith-api-client/identity';
import { FirmService } from '@6grain/masofa-monolith-api-client/dictionaries';
import { PasswordGenerator } from '@app/utils/password-generator';
import { getPasswordValidationErrorMessages, isPasswordValid } from '@app/utils/password-validator.util';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-user-individual-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    TabsModule,
    ProgressSpinnerModule,
    InputTextModule,
    CheckboxComponent,
    InputComponent,
    MsfSelectPaginated,
    DatePickerComponent,
    ServerTranslatePipe,
  ],
  templateUrl: './user-individual-dialog.component.html',
  styleUrls: ['./user-individual-dialog.component.scss'],
})
export class UserIndividualDialogComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() isSave: boolean = false;
  @Input() isSend: boolean = false;

  @Input() editingUserId: string | null = null;
  @Input() userData: any = null;

  @Input() rolesData: any[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onSave = new EventEmitter<any>();
  @Output() onSendLetter = new EventEmitter<void>();

  activeTab: string = '0';
  showPassword = false;
  showConfirmPassword = false;

  individualForm: any = this.getEmptyForm();

  private readonly passwordValue = signal('');
  private readonly confirmPasswordValue = signal('');
  private readonly innValue = signal('');
  private readonly formValidationTick = signal(0);

  readonly passwordErrorMessages = computed(() =>
    getPasswordValidationErrorMessages(this.passwordValue()),
  );

  readonly showPasswordMismatch = computed(() => {
    const password = this.passwordValue();
    const confirmPassword = this.confirmPasswordValue();
    return !!confirmPassword && !!password && password !== confirmPassword;
  });

  readonly isInnValid = computed(() => {
    const inn = this.innValue();
    if (!inn) {
      return true;
    }
    return /^\d{14}$/.test(inn);
  });

  readonly isFormValid = computed(() => {
    this.formValidationTick();
    const form = this.individualForm;
    const isBasicValid = !!form.login && !!form.email && !!form.lastName && !!form.firstName && !!form.organization;
    if (!this.isInnValid()) {
      return false;
    }

    if (!this.editingUserId) {
      return (
        isBasicValid &&
        !!form.password &&
        !!form.confirmPassword &&
        isPasswordValid(form.password) &&
        form.password === form.confirmPassword
      );
    }
    return isBasicValid;
  });

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    public userService: UserService,
    public firmService: FirmService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      if (!this.editingUserId) {
        this.individualForm = this.getEmptyForm();
      }
      this.syncPasswordValidationSignals();
    }

    if (changes['userData'] && this.userData) {
      this.mapUserDataToForm(this.userData);
      this.syncPasswordValidationSignals();
    }
  }

  touchFormValidation(): void {
    this.formValidationTick.update((value) => value + 1);
  }

  onPasswordChange(value: string): void {
    this.passwordValue.set(value ?? '');
    this.touchFormValidation();
  }

  onConfirmPasswordChange(value: string): void {
    this.confirmPasswordValue.set(value ?? '');
    this.touchFormValidation();
  }

  private syncPasswordValidationSignals(): void {
    this.passwordValue.set(this.individualForm.password ?? '');
    this.confirmPasswordValue.set(this.individualForm.confirmPassword ?? '');
    this.innValue.set(this.individualForm.inn ?? '');
    this.touchFormValidation();
  }

  onInnChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const originalValue = inputElement.value;
    let cleanValue = originalValue.replace(/\D/g, '');
    if (cleanValue.length > 14) {
      cleanValue = cleanValue.slice(0, 14);
    }
    if (inputElement.value !== cleanValue) {
      inputElement.value = cleanValue;
      inputElement.dispatchEvent(new Event('input')); 
    }
    this.individualForm.inn = cleanValue;
    this.innValue.set(cleanValue);
    this.touchFormValidation();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  save(): void {
    this.onSave.emit(this.individualForm);
  }

  generatePassword(): void {
    const password = PasswordGenerator.generateSimplePassword();
    this.individualForm.password = password;
    this.individualForm.confirmPassword = password;
    this.syncPasswordValidationSignals();

    this.copyToClipboard(password);
  }

  private copyToClipboard(text: string): void {
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('Сгенерировать пароль'),
            detail: this.translate.instant('Пароль скопирован в буфер обмена'),
          });
        });
      }
    } catch (_) {}
  }

  private getEmptyForm() {
    return {
      login: '',
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      secondName: '',
      organization: null,
      position: '',
      inn: '',
      phoneNumber: '',
      approved: true,
      emailConfirmed: false,
      selectedRoles: {},
      comment: '',
      deputyId: null,
      connectionBasis: '',
      parentId: null,
      isBlocked: false,
      blockoutStartDate: null,
      blockoutEndDate: null,
    };
  }

  private parseDatePreservingTime(dateStr: string | null): Date | null {
    if (!dateStr) return null;
    const localIsoStr = typeof dateStr === 'string' ? dateStr.substring(0, 19) : dateStr;
    return new Date(localIsoStr);
  }

  private mapUserDataToForm(data: any) {
    const selectedRoles: { [roleId: string]: boolean } = {};
    if (data.roles && Array.isArray(data.roles)) {
      data.roles.forEach((userRole: string) => {
        const role = this.rolesData.find(
          (r) => r.id === userRole || (r.name && r.name.toLowerCase() === userRole.toLowerCase()),
        );
        if (role) selectedRoles[role.id] = true;
        else selectedRoles[userRole] = true;
      });
    }

    this.individualForm = {
      login: data.userName || '',
      email: data.email || '',
      password: '',
      confirmPassword: '',
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      secondName: data.secondName || '',
      organization: data.organizationId || data.userBusinessId,
      position: data.position || '',
      inn: data.inn || data.pinfl || '',
      phoneNumber: data.phone || data.phoneNumber || '',
      approved: data.approved !== undefined ? data.approved : true,
      emailConfirmed: data.emailConfirmed !== undefined ? data.emailConfirmed : false,
      selectedRoles: selectedRoles,
      comment: data.comment || '',
      deputyId: data.deputyId || null,
      connectionBasis: data.connectionBasis || '',
      parentId: data.parentId || null,
      isBlocked: !!(data.scheduledLockoutStart && data.scheduledLockoutEnd),
      blockoutStartDate: this.parseDatePreservingTime(data.scheduledLockoutStart),
      blockoutEndDate: this.parseDatePreservingTime(data.scheduledLockoutEnd),
    };
  }
}
